import { Component, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { StorageService } from '../../core/storage/storageService';
import { Options } from '../../components/ui/filterable-table/options';
import { TableView } from '../../components/ui/filterable-table/tableView';
import { Router } from '@angular/router';
import { InvoiceTableViewComponent } from '../../components/invoices/invoice-table-view/invoice-table-view.component';
import { AccessManager } from '../../components/ui/access-manager/access-manager';
import { UIErrorHandler } from '../../core/uiErrorHandler';
import { InvoiceService } from '../../models/invoices/invoiceService';
import { SearchFilters } from '../../models/search/searchFilters';
import { InvoiceSettings } from '../../models/invoices/invoiceSettings';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { InvoiceNumberingComponent } from '../invoice-numbering/invoice-numbering.component';
import { AdminService } from '../../models/admin/adminService';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';

@Component({
  selector: 'app-manage-invoice',
  templateUrl: './manage-invoice.component.html',
  standalone: false,
})
export class ManageInvoiceComponent {
  @ViewChildren('invoiceTable')
  invoiceTables!: QueryList<InvoiceTableViewComponent>;
  activeTable: InvoiceTableViewComponent | undefined;
  invoiceSettings: InvoiceSettings = InvoiceSettings.createEmpty();
  loadingSpinnerRef: MatDialogRef<LoadingSpinnerComponent> | undefined;
  canCreateInvoices: boolean = false;
  tabStatus: string = 'sent';

  sentInvoicesTableView: TableView = new TableView(InvoiceTableViewComponent);
  sentInvoicesTableOptions: Options = new Options({
    selectAction: 'none',
    invoiceStatus: 'sent',
    filterable: true,
    allowEditing: true,
    pageSize: 10,
  });

  paidInvoicesTableView: TableView = new TableView(InvoiceTableViewComponent);
  paidInvoicesTableOptions: Options = new Options({
    selectAction: 'none',
    invoiceStatus: 'paid',
    filterable: true,
    allowEditing: true,
    pageSize: 10,
  });

  draftInvoicesTableView: TableView = new TableView(InvoiceTableViewComponent);
  draftInvoicesTableOptions: Options = new Options({
    selectAction: 'none',
    invoiceStatus: 'draft',
    filterable: true,
    allowEditing: true,
    pageSize: 10,
  });

  metricsFeatureFlagEnabled: boolean = false;

  constructor(
    public storage: StorageService,
    private router: Router,
    private access: AccessManager,
    private uiErrorHandler: UIErrorHandler,
    private invoiceService: InvoiceService,
    private dialog: MatDialog,
    private adminService: AdminService,
    public loadingSpinnerDialog: MatDialog,
  ) {
    this.access
      .getUserAccess([AccessManager.AUTH_CREATE_INVOICES])
      .then((access) => {
        this.canCreateInvoices = access[AccessManager.AUTH_CREATE_INVOICES];
      })
      .catch((message) => {
        this.uiErrorHandler.handleError(message);
      });

    this.adminService
      .getFeatureFlagByName('invoiceMetrics')
      .then((enabled) => {
        this.metricsFeatureFlagEnabled = enabled;
      })
      .catch((message) => {
        this.uiErrorHandler.handleError(message);
      });
  }

  ngAfterViewInit() {
    this.activeTable = this.invoiceTables.first;
    this.activeTable?.setOptions(this.sentInvoicesTableOptions);
    this.invoiceTables.changes.subscribe(
      (comps: QueryList<InvoiceTableViewComponent>) => {
        switch (this.tabStatus) {
          case 'sent':
            this.activeTable = comps.find(
              (table) => table.options.invoiceStatus === 'sent',
            );
            this.activeTable?.setOptions(this.sentInvoicesTableOptions);
            this.activeTable?.reload(
              '',
              new SearchFilters({}),
              1,
              this.sentInvoicesTableOptions.pageSize,
            );
            break;
          case 'paid':
            this.activeTable = comps.find(
              (table) => table.options.invoiceStatus === 'paid',
            );
            this.activeTable?.setOptions(this.paidInvoicesTableOptions);
            this.activeTable?.reload(
              '',
              new SearchFilters({}),
              1,
              this.paidInvoicesTableOptions.pageSize,
            );
            break;
          case 'draft':
            this.activeTable = comps.find(
              (table) => table.options.invoiceStatus === 'draft',
            );
            this.activeTable?.setOptions(this.draftInvoicesTableOptions);
            this.activeTable?.reload(
              '',
              new SearchFilters({}),
              1,
              this.draftInvoicesTableOptions.pageSize,
            );
            break;
        }
      },
    );
  }

  onCreateInvoiceClicked() {
    this.invoiceService
      .getInvoiceSettings(this.storage.getSelectedLocation()?.id || '')
      .then((invoiceSettings: InvoiceSettings) => {
        this.invoiceSettings = invoiceSettings;
        if (!this.invoiceSettings.generateInvoiceNumber) {
          let dialogRef: MatDialogRef<InvoiceNumberingComponent>;
          dialogRef = this.dialog.open(InvoiceNumberingComponent);
          dialogRef.componentInstance.invoiceNumber =
            this.invoiceSettings.nextNumber;
          dialogRef.componentInstance.initialInvoiceNumber =
            this.invoiceSettings.nextNumber;
          dialogRef.afterClosed().subscribe((result) => {
            if (result) {
              this.invoiceSettings.nextNumber = result;
            }
            this.createInvoice();
          });
        } else {
          this.createInvoice();
        }
      })
      .catch((error) => {
        console.error('error', error);
        this.uiErrorHandler.handleError(error);
      });
  }

  createInvoice() {
    this.loadingSpinnerRef = this.loadingSpinnerDialog.open(
      LoadingSpinnerComponent,
      {
        disableClose: true,
        panelClass: 'transparent-dialog',
        data: {
          mode: 'indeterminate',
        },
      },
    );
    this.invoiceService
      .createInvoice(
        this.storage.getSelectedLocation()?.id || '',
        this.invoiceSettings,
      )
      .then((invoiceId) => {
        this.storage.setSelectedInvoiceId(invoiceId);
        this.loadingSpinnerRef?.close();
        this.router.navigate(['/merchant/invoices/create']);
      })
      .catch((error) => {
        console.error('error', error);
        this.uiErrorHandler.handleError(error);
      });
  }

  onInvoiceSettingsClicked() {
    this.router.navigate(['/merchant/invoices/settings']);
  }

  onTabSelected(label: string) {
    switch (label) {
      case 'Sent Invoices':
        this.tabStatus = 'sent';
        break;
      case 'Paid Invoices':
        this.tabStatus = 'paid';
        break;
      case 'Draft Invoices':
        this.tabStatus = 'draft';
        break;
    }
  }
}
